import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { jsPDF } from 'jspdf';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";

export default function CommitmentSigner({ onSigned, tanda }) {
  const { user, api } = useAuth();
  const sigCanvas = useRef({});
  const [typedName, setTypedName] = useState('');
  const [ineFile, setIneFile] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signMethod, setSignMethod] = useState('draw');
  const [address, setAddress] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const clear = () => sigCanvas.current.clear();

  const handleIneChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setIneFile(e.target.files[0]);
    }
  };

  const getContractText = () => {
      const amountText = tanda ? `$${tanda.contribution_amount}` : "[CANTIDAD]";
      const freqText = tanda ? tanda.periodicity.toUpperCase() : "[FRECUENCIA]";
      const startDate = tanda ? new Date(tanda.start_date).toLocaleDateString() : "[FECHA INICIO]";
      const endDate = tanda ? (tanda.end_date ? new Date(tanda.end_date).toLocaleDateString() : "Indefinido") : "[FECHA TÉRMINO]";

      return `
CONTRATO DE ADHESIÓN Y CARTA COMPROMISO DE DONACIÓN RECURRENTE
PROGRAMA SOCIAL: "TANDUCIBLES" – ATLASST A.C.
Lugar y Fecha: México, a ${new Date().toLocaleDateString('es-MX')}

A LA ATENCIÓN DEL CONSEJO DIRECTIVO DE ATLASST A.C.

Por medio de la presente, quien suscribe, C. ${user.full_name}, identificado con INE/Pasaporte número ${idNumber || '[NUMERO ID]'}, con domicilio en ${address || '[DIRECCIÓN]'}, manifiesto mi voluntad libre, informada e irrevocable de adherirme al programa de colaboración comunitaria denominado "TANDUCIBLES".

Declaro entender que ATLASST A.C. es una asociación civil y Donataria Autorizada, cuyo objeto es la asistencia social. En consecuencia, reconozco que mis aportaciones tienen el carácter jurídico de donativos destinados al fondo común.

Bajo protesta de decir verdad, suscribo los siguientes compromisos:

PRIMERA: DEL COMPROMISO DE DONACIÓN. Me comprometo a realizar una aportación periódica por la cantidad de ${amountText} M.N., con una frecuencia ${freqText}. Este compromiso tendrá una vigencia obligatoria durante todo el ciclo del "Tanducible" al que he sido asignado, iniciando el ${startDate} y concluyendo el ${endDate}.

SEGUNDA: PUNTUALIDAD. Me obligo a realizar dicha aportación en las fechas estipuladas.

TERCERA: PENALIZACIÓN POR MORA. En caso de incumplimiento, acepto pagar una Pena Convencional del 10% sobre el monto vencido.

CUARTA: RESCISIÓN. Si acumulo 2 faltas de pago consecutivas, acepto mi exclusión del programa y la pérdida de beneficios futuros.

QUINTA: IMPACTO COLECTIVO. Reconozco que el incumplimiento afecta a mi comunidad y asumo la responsabilidad ética.

SEXTA: ACEPTACIÓN DEL REGLAMENTO. Manifiesto haber leído y aceptado el ANEXO "A" (Reglamento de Asignación de Recursos).

SÉPTIMA: JURISDICCIÓN. Me someto a los reglamentos internos de ATLASST A.C.
      `;
  };

  const generatePDF = async () => {
    if (!ineFile || !address || !idNumber || !acceptedTerms) {
      toast.error("Por favor completa todos los campos y acepta los términos");
      return;
    }

    if (signMethod === 'draw' && sigCanvas.current.isEmpty()) {
       toast.error("Por favor firma el documento");
       return;
    }
    
    if (signMethod === 'type' && !typedName.trim()) {
        toast.error("Por favor escribe tu nombre");
        return;
    }

    setSigning(true);
    try {
      // 1. Convert INE to Base64
      const ineBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(ineFile);
      });

      // 2. Get Signature Image
      let signatureImage;
      if (signMethod === 'draw') {
         signatureImage = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
      } else {
         const canvas = document.createElement('canvas');
         canvas.width = 400;
         canvas.height = 100;
         const ctx = canvas.getContext('2d');
         ctx.font = '30px serif';
         ctx.fillStyle = 'black';
         ctx.fillText(typedName, 10, 50);
         signatureImage = canvas.toDataURL('image/png');
      }

      // 3. Generate PDF
      const doc = new jsPDF();
      const text = getContractText(); // Get the plain text

      doc.setFontSize(10);
      
      // Split text to fit
      const splitText = doc.splitTextToSize(text, 170);
      let y = 20;
      
      // Add text page by page
      for (let i = 0; i < splitText.length; i++) {
        if (y > 270) {
            doc.addPage();
            y = 20;
        }
        doc.text(splitText[i], 20, y);
        y += 5;
      }
      
      // Add Signature
      y += 10;
      if (y > 240) { doc.addPage(); y = 40; }
      
      doc.text("FIRMA DEL PARTICIPANTE:", 20, y);
      doc.addImage(signatureImage, 'PNG', 20, y + 5, 50, 25);
      doc.text("__________________________", 20, y + 35);
      doc.text(user.full_name, 20, y + 40);
      
      // Add INE
      doc.addPage();
      doc.text("Anexo: Identificación Oficial (INE)", 20, 20);
      try {
        doc.addImage(ineBase64, 'JPEG', 20, 30, 160, 100);
      } catch (imgError) {
        console.error("Error adding INE image", imgError);
        // Fallback or skip
        doc.text("[Error adjuntando imagen de INE]", 20, 40);
      }

      const pdfBlob = doc.output('blob');

      // 4. Upload PDF
        const { data: sig } = await api.get('/tandas/cloudinary-signature');
        
        const formData = new FormData();
        formData.append("file", pdfBlob, "contrato_tanducibles.pdf");
        formData.append("api_key", sig.api_key);
        formData.append("timestamp", sig.timestamp);
        formData.append("signature", sig.signature);
        formData.append("folder", "tanducibles_contracts");

        const res = await fetch(
            `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
            { method: "POST", body: formData }
        );
        
        if (!res.ok) throw new Error("Upload failed");
        
        const cloudData = await res.json();
        
        toast.success("Contrato generado correctamente");
        if(onSigned) onSigned(cloudData.secure_url);

    } catch (error) {
      console.error(error);
      toast.error("Error al generar documento: " + error.message);
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="space-y-6 max-h-[80vh] overflow-y-auto p-2">
        <div className="space-y-4">
            <h3 className="font-bold border-b pb-2">1. Datos Personales</h3>
            <div className="grid gap-4">
                <div className="space-y-2">
                    <Label>Dirección Completa</Label>
                    <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Calle, Número, Colonia, CP, Ciudad" />
                </div>
                <div className="space-y-2">
                    <Label>Número de INE / Pasaporte</Label>
                    <Input value={idNumber} onChange={(e) => setIdNumber(e.target.value)} placeholder="0000000000000" />
                </div>
            </div>
        </div>

        <div className="space-y-4">
             <h3 className="font-bold border-b pb-2">2. Revisión del Contrato</h3>
             <ScrollArea className="h-64 w-full rounded-md border p-4 bg-gray-50 text-xs whitespace-pre-wrap">
                {getContractText()}
             </ScrollArea>
        </div>

        <div className="space-y-4">
            <h3 className="font-bold border-b pb-2">3. Identificación</h3>
            <div className="space-y-2">
                <Label>Foto de INE (Frente)</Label>
                <Input type="file" accept="image/*" onChange={handleIneChange} />
            </div>
        </div>

        <div className="space-y-4">
            <h3 className="font-bold border-b pb-2">4. Firma</h3>
            <Tabs defaultValue="draw" onValueChange={setSignMethod}>
                <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="draw">Dibujar</TabsTrigger>
                    <TabsTrigger value="type">Escribir</TabsTrigger>
                </TabsList>
                <TabsContent value="draw" className="space-y-2">
                    <div className="border rounded-md bg-white cursor-crosshair">
                        <SignatureCanvas 
                            ref={sigCanvas} 
                            canvasProps={{width: 500, height: 200, className: 'sigCanvas w-full h-32'}} 
                        />
                    </div>
                    <Button variant="outline" size="sm" onClick={clear}>Limpiar</Button>
                </TabsContent>
                <TabsContent value="type" className="space-y-2">
                    <Input 
                        placeholder="Nombre completo" 
                        value={typedName}
                        onChange={(e) => setTypedName(e.target.value)}
                        className="font-serif italic"
                    />
                </TabsContent>
            </Tabs>
        </div>

        <div className="flex items-center space-x-2 py-4">
            <Checkbox id="terms" checked={acceptedTerms} onCheckedChange={setAcceptedTerms} />
            <Label htmlFor="terms" className="text-sm">
                He leído y acepto el Contrato de Adhesión y el Reglamento de Asignación.
            </Label>
        </div>

        <Button onClick={generatePDF} disabled={signing || !ineFile} className="w-full bg-primary text-primary-foreground">
            {signing ? 'Generando PDF...' : 'Firmar y Unirme'}
        </Button>
    </div>
  );
}
